import { useEffect, useState } from "react"
import {
  Button,
  ButtonGroup,
  Form,
  FormGroup,
  Label,
  Row,
  Col,
} from "reactstrap"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import ThemeModal from "components/Common/ThemeModal"

const toIsoDate = date => {
  if (!date) return ""
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, "0")
  const dd = String(date.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

const buildInitialValues = () => ({
  print_date: "",
  with_pad: true,
  self_signature: false,
})

const parseIsoDate = iso => {
  if (!iso) return null
  const parts = String(iso).split("-")
  if (parts.length !== 3) return null
  const [y, m, d] = parts.map(Number)
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d)
}

const BooleanRadioGroup = ({
  label,
  description,
  value,
  onChange,
  disabled = false,
}) => {
  return (
    <FormGroup>
      <Label className="fw-semibold d-block mb-2">{label}</Label>
      <ButtonGroup size="sm">
        <Button
          type="button"
          color={value === true ? "primary" : "light"}
          outline={value !== true}
          onClick={() => onChange(true)}
          disabled={disabled}
        >
          Yes
        </Button>
        <Button
          type="button"
          color={value === false ? "primary" : "light"}
          outline={value !== false}
          onClick={() => onChange(false)}
          disabled={disabled}
        >
          No
        </Button>
      </ButtonGroup>
      {description ? (
        <div className="text-muted small mt-1">{description}</div>
      ) : null}
    </FormGroup>
  )
}

const LoanPdfPrintOptionsModal = ({
  isOpen,
  toggle,
  onSubmit,
  isSubmitting = false,
  title = "Print Options",
}) => {
  const [values, setValues] = useState(buildInitialValues())

  useEffect(() => {
    if (isOpen) {
      setValues(buildInitialValues())
    }
  }, [isOpen])

  const handleDateChange = date => {
    setValues(current => ({
      ...current,
      print_date: toIsoDate(date),
    }))
  }

  const setBoolean = (name, nextValue) => {
    setValues(current => ({
      ...current,
      [name]: nextValue,
    }))
  }

  const handleSubmit = event => {
    event.preventDefault()
    onSubmit({
      print_date: values.print_date || null,
      with_pad: values.with_pad,
      self_signature: values.self_signature,
    })
  }

  return (
    <ThemeModal
      isOpen={isOpen}
      toggle={toggle}
      title={title}
      size="md"
      footer={
        <div className="d-flex justify-content-end gap-2 w-100">
          <Button color="light" onClick={toggle} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Generating..." : "Generate PDF"}
          </Button>
        </div>
      }
    >
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label for="print_date" className="fw-semibold">
            Print Date
          </Label>
          <DatePicker
            id="print_date"
            selected={parseIsoDate(values.print_date)}
            onChange={handleDateChange}
            dateFormat="MMMM d, yyyy"
            placeholderText="Select date"
            className="form-control"
            isClearable
            disabled={isSubmitting}
            autoComplete="off"
            wrapperClassName="d-block"
          />
          <small className="text-muted">
            Clear the field to print without a date.
          </small>
        </FormGroup>

        <Row className="mt-2">
          <Col xs="6">
            <BooleanRadioGroup
              name="with_pad"
              label="Print with Pad"
              description="Show company header on the page."
              value={values.with_pad}
              onChange={next => setBoolean("with_pad", next)}
              disabled={isSubmitting}
            />
          </Col>
          <Col xs="6">
            <BooleanRadioGroup
              name="self_signature"
              label="Self Signature"
              description="Use your name & designation as authorized signature."
              value={values.self_signature}
              onChange={next => setBoolean("self_signature", next)}
              disabled={isSubmitting}
            />
          </Col>
        </Row>

      </Form>
    </ThemeModal>
  )
}

export default LoanPdfPrintOptionsModal
